<template>
  <div class="col-12">
    <div class="card mb-5" style="margin-top: 4rem; overflow: visible">
      <div
        class="card-header p-0 position-relative mt-n4 mx-3 z-index-2"
        style="height: 60px"
      >
        <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <div class="d-flex justify-content-between align-items-center">
            <h6 class="text-white text-capitalize ps-3 pe-3 mb-0">
              {{ isEdit ? t('canteen.edit_product') : t('canteen.add_product') }}
            </h6>
          </div>
        </div>
      </div>

      <div class="card-body px-0 pb-2 pe-3 position-relative">
        <div class="row justify-content-center">
          <div class="col-md-8 ms-3">
            <form @submit.prevent="handleSubmit" @keydown.enter.prevent>
              <div class="row g-3">
                <div class="col-md-12">
                  <label for="name" class="bmd-label-float">{{ t('canteen.product_name') }}</label>
                  <input
                    id="name"
                    v-model.trim="formData.name"
                    type="text"
                    class="form-control borderless-input"
                    :class="{ 'is-invalid': errors.name }"
                    required
                  />
                  <div class="invalid-feedback">{{ errors.name }}</div>
                </div>

                <div class="col-md-12">
                  <label for="description" class="bmd-label-float">
                    {{ t('canteen.product_description') }}
                  </label>
                  <textarea
                    id="description"
                    v-model.trim="formData.description"
                    rows="3"
                    class="form-control borderless-input"
                    :class="{ 'is-invalid': errors.description }"
                  ></textarea>
                  <div class="invalid-feedback">{{ errors.description }}</div>
                </div>

                <div class="col-md-6">
                  <label for="price" class="bmd-label-float">{{ t('canteen.product_price') }}</label>
                  <div class="input-group">
                    <span class="input-group-text">{{ t('canteen.lyd_abbr') }}</span>
                    <input
                      id="price"
                      v-model.number="formData.price"
                      type="number"
                      min="0"
                      step="0.01"
                      class="form-control borderless-input"
                      :class="{ 'is-invalid': errors.price }"
                      required
                    />
                  </div>
                  <div class="invalid-feedback">{{ errors.price }}</div>
                </div>

                <div class="col-md-6">
                  <label for="is_available" class="bmd-label-float">
                    {{ t('canteen.availability') }}
                  </label>
                  <select
                    id="is_available"
                    v-model="formData.is_available"
                    class="form-select borderless-input"
                    :class="{ 'is-invalid': errors.is_available }"
                  >
                    <option :value="true">{{ t('canteen.available') }}</option>
                    <option :value="false">{{ t('canteen.not_available') }}</option>
                  </select>
                  <div class="invalid-feedback">{{ errors.is_available }}</div>
                </div>

                <div class="col-md-12">
                  <label for="allergy_warnings" class="bmd-label-float">
                    {{ t('canteen.allergy_warnings') }}
                  </label>
                  <textarea
                    id="allergy_warnings"
                    v-model.trim="formData.allergy_warnings"
                    rows="2"
                    class="form-control borderless-input"
                    :class="{ 'is-invalid': errors.allergy_warnings }"
                    :placeholder="t('canteen.allergy_placeholder')"
                  ></textarea>
                  <div class="invalid-feedback">{{ errors.allergy_warnings }}</div>
                  <small class="text-muted">{{ t('canteen.comma_separated') }}</small>
                </div>

                <div class="col-md-12">
                  <label class="form-label">{{ t('canteen.product_image') }}</label>

                  <div
                    class="card dashed-border p-3 text-center cursor-pointer image-upload-box position-relative"
                    @click="openFilePicker"
                  >
                    <input
                      ref="fileInput"
                      id="image"
                      type="file"
                      accept="image/*"
                      style="display: none"
                      @change="handleImageUpload"
                    />

                    <div v-if="loadingImage" class="text-center py-4">
                      <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">{{ t('canteen.loading') }}</span>
                      </div>
                    </div>

                    <template v-else>
                      <img
                        :src="displayImage"
                        class="img-fluid mx-auto d-block upload-preview-image"
                        :alt="formData.name || t('canteen.product_image')"
                        @error="handlePreviewImageError"
                      />

                      <div class="mt-3">
                        <p class="text-muted mb-1">{{ t('canteen.click_to_upload') }}</p>
                        <small class="text-muted">{{ t('canteen.image_requirements') }}</small>
                      </div>
                    </template>

                    <button
                      v-if="hasSelectedOrStoredImage"
                      type="button"
                      @click.stop="clearImage"
                      class="btn btn-sm btn-danger position-absolute top-0 end-0 m-2"
                    >
                      <i class="fas fa-trash"></i>
                    </button>
                  </div>

                  <div v-if="errors.image" class="invalid-feedback d-block">
                    {{ errors.image }}
                  </div>

                  <small class="text-muted d-block mt-1">
                    {{ t('canteen.image_requirements') }}
                  </small>
                </div>

                <div v-if="formData.name" class="col-md-12">
                  <div class="card mt-4 preview-card">
                    <div class="card-header">
                      <h6 class="mb-0">{{ t('canteen.product_preview') }}</h6>
                    </div>

                    <div class="card-body">
                      <div class="row align-items-center">
                        <div class="col-md-4 mb-3 mb-md-0">
                          <img
                            :src="displayImage"
                            class="img-fluid rounded product-preview-image"
                            :alt="formData.name"
                            @error="handlePreviewImageError"
                          />
                        </div>

                        <div class="col-md-8">
                          <h5 class="mb-2">{{ formData.name }}</h5>

                          <p v-if="formData.description" class="text-muted mb-2">
                            {{ formData.description }}
                          </p>

                          <p class="h5 text-primary mb-2">
                            {{ normalizedPrice }} {{ t('canteen.lyd_abbr') }}
                          </p>

                          <span
                            class="badge"
                            :class="formData.is_available ? 'bg-success' : 'bg-secondary'"
                          >
                            {{
                              formData.is_available
                                ? t('canteen.available')
                                : t('canteen.not_available')
                            }}
                          </span>

                          <div v-if="formData.allergy_warnings" class="mt-2">
                            <small class="text-danger">
                              <i class="fas fa-exclamation-triangle"></i>
                              {{ t('canteen.allergy_warnings_label') }}:
                              {{ formData.allergy_warnings }}
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-12 mt-4">
                  <div class="d-flex gap-3">
                    <button
                      type="submit"
                      class="btn btn-primary btn-sm flex-grow-1"
                      :disabled="loading"
                    >
                      <span v-if="loading">
                        <i class="fas fa-spinner fa-spin ms-2"></i>
                        {{ t('canteen.saving') }}
                      </span>
                      <span v-else>
                        <i class="fas fa-save ms-2"></i>
                        {{
                          isEdit
                            ? t('canteen.update_product')
                            : t('canteen.add_product_button')
                        }}
                      </span>
                    </button>

                    <router-link
                      :to="{ name: 'canteen.products' }"
                      class="btn btn-outline-secondary btn-sm flex-grow-1"
                    >
                      <i class="fas fa-times ms-2"></i>
                      {{ t('canteen.cancel') }}
                    </router-link>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n'
import api from '@/api'
import defaultProductImage from '@/assets/img/default-product.png'

const { t, locale } = useI18n()
const toast = useToast()
const route = useRoute()
const router = useRouter()

const loading = ref(false)
const loadingImage = ref(false)
const fileInput = ref(null)
const previewImage = ref(null)

const isEdit = computed(() => route.name === 'canteen.products.edit')
const DEFAULT_PRODUCT_IMAGE = defaultProductImage

const formData = ref({
  name: '',
  description: '',
  price: 0,
  is_available: true,
  allergy_warnings: '',
  image: null,
  image_url: null
})

const errors = ref({})

const normalizedPrice = computed(() => {
  const price = Number(formData.value.price || 0)
  return Number.isFinite(price) ? price.toFixed(2).replace(/\.00$/, '') : '0'
})

const hasSelectedOrStoredImage = computed(() => {
  return !!(previewImage.value || formData.value.image_url)
})

const displayImage = computed(() => {
  if (previewImage.value) return previewImage.value
  if (formData.value.image_url) return formData.value.image_url
  return DEFAULT_PRODUCT_IMAGE
})

const openFilePicker = () => {
  fileInput.value?.click()
}

const handlePreviewImageError = (event) => {
  event.target.src = DEFAULT_PRODUCT_IMAGE
}

const handleImageUpload = (event) => {
  const file = event.target.files?.[0]

  if (!file) {
    resetSelectedImage()
    return
  }

  const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp']
  const maxSize = 2 * 1024 * 1024

  if (!validTypes.includes(file.type.toLowerCase()) || file.size > maxSize) {
    errors.value.image = t('canteen.image_type_size_error')
    resetSelectedImage()
    return
  }

  formData.value.image = file
  delete errors.value.image

  loadingImage.value = true

  const reader = new FileReader()
  reader.onload = (e) => {
    previewImage.value = e.target?.result || null
    loadingImage.value = false
  }
  reader.onerror = () => {
    loadingImage.value = false
    previewImage.value = null
    errors.value.image = t('canteen.image_type_size_error')
  }

  reader.readAsDataURL(file)
}

const clearImage = () => {
  formData.value.image = null
  previewImage.value = null

  if (!isEdit.value) {
    formData.value.image_url = null
  }

  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

const resetSelectedImage = () => {
  formData.value.image = null
  previewImage.value = null

  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

const validateForm = () => {
  errors.value = {}

  if (!formData.value.name.trim()) {
    errors.value.name = t('canteen.product_name_required')
  }

  if (
    formData.value.price === '' ||
    formData.value.price === null ||
    Number(formData.value.price) <= 0
  ) {
    errors.value.price = t('canteen.price_positive')
  }

  return Object.keys(errors.value).length === 0
}

function extractApiMessage(error, fallbackKey = 'errors.generic') {
  const data = error?.response?.data

  if (data?.message) {
    if (typeof data.message === 'object' && data.message !== null) {
      const loc = (data.locale || locale.value || 'en').toLowerCase().startsWith('ar')
        ? 'ar'
        : 'en'
      if (typeof data.message[loc] === 'string') return data.message[loc]
    }

    if (typeof data.message === 'string') return data.message
  }

  if (error?.response?.statusText) return error.response.statusText
  return t(fallbackKey)
}

function notifyError(error, fallbackKey = 'errors.generic') {
  const status = error?.response?.status

  if (status === 401) {
    return toast.error(t('auth.unauthorized', 'Unauthorized'))
  }

  if (status === 403) {
    return toast.error(t('auth.forbidden', 'Forbidden'))
  }

  return toast.error(extractApiMessage(error, fallbackKey))
}

const handleSubmit = async () => {
  if (!validateForm()) {
    toast.warning(t('canteen.form_fix_errors'))
    return
  }

  try {
    loading.value = true

    const payload = new FormData()
    payload.append('name', formData.value.name)
    payload.append('description', formData.value.description || '')
    payload.append('price', String(formData.value.price))
    payload.append('is_available', formData.value.is_available ? '1' : '0')
    payload.append('allergy_warnings', formData.value.allergy_warnings || '')

    if (formData.value.image instanceof File) {
      payload.append('image', formData.value.image)
    }

    let resp

    if (isEdit.value) {
      payload.append('_method', 'PUT')
      resp = await api.post(`/canteen/products/${route.params.id}`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
    } else {
      resp = await api.post('/canteen/products', payload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
    }

    const status = resp?.status ?? resp?.data?.status ?? null

    if (!status || ![200, 201, 204].includes(status)) {
      if (status === 403) {
        toast.error(t('auth.forbidden', 'Forbidden'))
        return
      }

      toast.error(
        resp?.data?.message ||
          (isEdit.value
            ? t('canteen.product_update_failed')
            : t('canteen.product_add_failed'))
      )
      return
    }

    toast.success(
      resp?.data?.message ||
        (isEdit.value ? t('canteen.product_updated') : t('canteen.product_added'))
    )

    router.push({ name: 'canteen.products' })
  } catch (error) {
    if (error?.response?.status === 422) {
      errors.value = error.response?.data?.errors || {}
      toast.error(t('canteen.form_fix_errors'))
    } else {
      notifyError(
        error,
        isEdit.value ? 'canteen.product_update_failed' : 'canteen.product_add_failed'
      )
    }
  } finally {
    loading.value = false
  }
}

const fetchProduct = async (id) => {
  try {
    const resp = await api.get(`/canteen/products/${id}`)
    const product = resp?.data?.data || resp?.data

    formData.value = {
      name: product?.name || '',
      description: product?.description || '',
      price: product?.price ?? 0,
      is_available: !!product?.is_available,
      allergy_warnings: product?.allergy_warnings || '',
      image: null,
      image_url: product?.image_url || (product?.image ? `/storage/${product.image}` : null)
    }

    previewImage.value = null
  } catch (error) {
    notifyError(error, 'canteen.product_load_failed')
    router.push({ name: 'canteen.products' })
  }
}

onMounted(() => {
  if (isEdit.value && route.params.id) {
    fetchProduct(route.params.id)
  }
})
</script>

<style scoped>
.borderless-input {
  border: none;
  border-bottom: 1px solid #d2d2d2;
  border-radius: 0;
  padding-left: 0;
  padding-right: 0;
  background-color: transparent;
}

.borderless-input:focus {
  border-bottom: 1px solid #4e73df;
  box-shadow: none;
  background-color: transparent;
}

.bmd-label-float {
  position: relative;
  margin-bottom: 0.5rem;
  color: #6c757d;
  font-size: 0.875rem;
}

.dashed-border {
  border: 2px dashed #dee2e6;
  border-radius: 12px;
}

.cursor-pointer {
  cursor: pointer;
}

.is-invalid {
  border-color: #dc3545 !important;
}

.invalid-feedback {
  display: block;
  width: 100%;
  margin-top: 0.25rem;
  font-size: 0.875em;
  color: #dc3545;
}

.image-upload-box {
  min-height: 280px;
  background: #fafafa;
  transition: 0.2s ease;
}

.image-upload-box:hover {
  border-color: #4e73df;
  background: #f8fbff;
}

.upload-preview-image {
  max-height: 190px;
  max-width: 100%;
  object-fit: contain;
  border-radius: 12px;
}

.product-preview-image {
  width: 100%;
  max-height: 150px;
  object-fit: cover;
  background: #f8f9fa;
}

.preview-card {
  border-radius: 14px;
}
</style>